import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import clsx from "clsx";
import { ArrowTopRightOnSquareIcon, ArrowUpTrayIcon, PlusIcon } from "@heroicons/react/24/outline";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { TextareaInput, TextInput } from "@/components/ui/Field";
import { Spinner } from "@/components/ui/Spinner";
import { setGlobalUploadSuppressed } from "@/features/globalUpload/globalUploadSuppress";
import { ApiError } from "@/lib/apiClient";
import "./huellePlan.css";
import {
  deleteMaintenancePlan,
  deleteMaintenancePlanFireSafety,
  deleteMaintenancePlanLegacy,
  getSubLocationPlans,
  patchMaintenancePlan,
  uploadMaintenancePlanFireSafety,
  uploadMaintenancePlanLegacy,
  uploadSubLocationPlan,
} from "@/lib/wartungen";
import {
  buildRenderedPlanImageUrl,
  clampMarkerOverlayScale,
  formatFileSize,
  isAllowedPlanFile,
  normalizePlanRecord,
  type MaintenancePlanRecord,
} from "./planModel";

const PLAN_FILE_ACCEPT =
  "application/pdf,image/jpeg,image/png,image/webp,image/tiff,.pdf,.jpg,.jpeg,.png,.webp,.tif,.tiff";

type PlanSection = "plan" | "legacy" | "fire";
type PendingAction =
  | "upload"
  | "save"
  | "delete"
  | "legacy-upload"
  | "legacy-delete"
  | "fire-upload"
  | "fire-delete";

export interface PlanManagementDialogProps {
  open: boolean;
  onClose: () => void;
  heroAddressId: number | null;
  subLocationId: number | null;
  subLocationLabel: string;
  onPlansChanged: () => void;
}

export function PlanManagementDialog({
  open,
  onClose,
  heroAddressId,
  subLocationId,
  subLocationLabel,
  onPlansChanged,
}: PlanManagementDialogProps) {
  const [plans, setPlans] = useState<MaintenancePlanRecord[]>([]);
  const [managedPlanId, setManagedPlanId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [section, setSection] = useState<PlanSection>("plan");

  const [draftFile, setDraftFile] = useState<File | null>(null);
  const [draftPreviewUrl, setDraftPreviewUrl] = useState("");
  const [title, setTitle] = useState("");
  const [version, setVersion] = useState("");
  const [page, setPage] = useState("1");
  const [description, setDescription] = useState("");
  const [isActive, setIsActive] = useState(false);
  const [activateAfterUpload, setActivateAfterUpload] = useState(true);
  const [markerScalePct, setMarkerScalePct] = useState(100);

  const [legacyDraftFile, setLegacyDraftFile] = useState<File | null>(null);
  const [legacyDraftPreviewUrl, setLegacyDraftPreviewUrl] = useState("");
  const [legacyPage, setLegacyPage] = useState("1");

  const [fireSafetyDraftFile, setFireSafetyDraftFile] = useState<File | null>(null);
  const [fireSafetyDraftPreviewUrl, setFireSafetyDraftPreviewUrl] = useState("");
  const [fireSafetyPage, setFireSafetyPage] = useState("1");

  const noticeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadSeq = useRef(0);
  const syncedPlanKey = useRef("");
  const openRef = useRef(open);
  openRef.current = open;
  const busy = pending != null;

  const managedPlan = useMemo(
    () => plans.find((plan) => Number(plan.id) === Number(managedPlanId)) || null,
    [plans, managedPlanId],
  );

  const orderedPlans = useMemo(() => {
    return [...plans].sort((a, b) => Number(Boolean(b.is_active)) - Number(Boolean(a.is_active)));
  }, [plans]);

  const flash = useCallback((message: string) => {
    setNotice(message);
    if (noticeTimer.current) clearTimeout(noticeTimer.current);
    noticeTimer.current = setTimeout(() => setNotice(null), 2800);
  }, []);

  useEffect(
    () => () => {
      if (noticeTimer.current) clearTimeout(noticeTimer.current);
    },
    [],
  );

  // Solange der Plan-Dialog offen ist, gehört ein Datei-Drop der Plan-Ablage
  // und nicht dem globalen Dateiablage-Fenster.
  useEffect(() => {
    if (!open) return;
    setGlobalUploadSuppressed(true);
    return () => setGlobalUploadSuppressed(false);
  }, [open]);

  const loadPlans = useCallback(async () => {
    if (!heroAddressId || !subLocationId) {
      setPlans([]);
      return;
    }
    const seq = ++loadSeq.current;
    setLoading(true);
    setError(null);
    try {
      const raw = await getSubLocationPlans(heroAddressId, subLocationId);
      if (seq !== loadSeq.current || !openRef.current) return;
      const normalized = raw
        .map((p) => normalizePlanRecord(p as MaintenancePlanRecord))
        .filter(Boolean) as MaintenancePlanRecord[];
      setPlans(normalized);
      if (normalized.length === 0) {
        setCreating(true);
        setSection("plan");
        setDraftFile(null);
        setLegacyDraftFile(null);
        setFireSafetyDraftFile(null);
        setTitle("");
        setVersion("");
        setPage("1");
        setDescription("");
        setActivateAfterUpload(true);
      }
      setManagedPlanId((prev) => {
        if (prev && normalized.some((p) => Number(p.id) === Number(prev))) return prev;
        return normalized.find((p) => p.is_active)?.id ?? normalized[0]?.id ?? null;
      });
    } catch (err) {
      if (seq !== loadSeq.current || !openRef.current) return;
      setError(err instanceof ApiError ? err.message : "Pläne konnten nicht geladen werden.");
      setPlans([]);
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [heroAddressId, subLocationId]);

  useEffect(() => {
    if (!open) {
      setCreating(false);
      setSection("plan");
      setDraftFile(null);
      setLegacyDraftFile(null);
      setFireSafetyDraftFile(null);
      setNotice(null);
      setError(null);
      setPending(null);
      return;
    }
    void loadPlans();
  }, [open, loadPlans]);

  useEffect(() => {
    if (draftFile || creating) {
      syncedPlanKey.current = "";
      return;
    }
    const key = managedPlan?.id != null ? String(managedPlan.id) : "none";
    if (syncedPlanKey.current === key) return;
    syncedPlanKey.current = key;
    if (!managedPlan) {
      setTitle("");
      setVersion("");
      setPage("1");
      setDescription("");
      setIsActive(false);
      setMarkerScalePct(100);
      setLegacyPage("1");
      setFireSafetyPage("1");
      return;
    }
    setTitle(String(managedPlan.title || ""));
    setVersion(String(managedPlan.version || ""));
    setPage(String(managedPlan.page || managedPlan.page_number || 1));
    setDescription(String(managedPlan.description || ""));
    setIsActive(Boolean(managedPlan.is_active));
    setMarkerScalePct(Math.round(clampMarkerOverlayScale(managedPlan.marker_overlay_scale) * 100));
    setLegacyPage(String(managedPlan.legacy_plan_page || managedPlan.legacy_plan_page_number || 1));
    setFireSafetyPage(String(managedPlan.fire_safety_plan_page || managedPlan.fire_safety_plan_page_number || 1));
  }, [managedPlan, draftFile, creating]);

  useEffect(() => {
    if (!draftFile) {
      setDraftPreviewUrl("");
      return;
    }
    const url = URL.createObjectURL(draftFile);
    setDraftPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [draftFile]);

  useEffect(() => {
    if (!legacyDraftFile) {
      setLegacyDraftPreviewUrl("");
      return;
    }
    const url = URL.createObjectURL(legacyDraftFile);
    setLegacyDraftPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [legacyDraftFile]);

  useEffect(() => {
    if (!fireSafetyDraftFile) {
      setFireSafetyDraftPreviewUrl("");
      return;
    }
    const url = URL.createObjectURL(fireSafetyDraftFile);
    setFireSafetyDraftPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [fireSafetyDraftFile]);

  const metaDirty = useMemo(() => {
    if (!managedPlan || creating) return false;
    return (
      title !== String(managedPlan.title || "") ||
      version !== String(managedPlan.version || "") ||
      page !== String(managedPlan.page || managedPlan.page_number || 1) ||
      description !== String(managedPlan.description || "") ||
      isActive !== Boolean(managedPlan.is_active) ||
      markerScalePct !== Math.round(clampMarkerOverlayScale(managedPlan.marker_overlay_scale) * 100)
    );
  }, [managedPlan, creating, title, version, page, description, isActive, markerScalePct]);

  const createDirty =
    creating &&
    (draftFile != null ||
      title.trim() !== "" ||
      version.trim() !== "" ||
      description.trim() !== "" ||
      page.trim() !== "1" ||
      !activateAfterUpload);

  function resetCreateFields() {
    setDraftFile(null);
    setTitle("");
    setVersion("");
    setPage("1");
    setDescription("");
    setActivateAfterUpload(true);
  }

  function confirmDiscardDrafts(): boolean {
    const attachmentDirty = legacyDraftFile != null || fireSafetyDraftFile != null;
    if (!createDirty && !attachmentDirty && !metaDirty) return true;
    return window.confirm("Ungespeicherte Änderungen verwerfen?");
  }

  function startNewPlan() {
    if (creating || busy) return;
    if (!confirmDiscardDrafts()) return;
    setLegacyDraftFile(null);
    setFireSafetyDraftFile(null);
    setCreating(true);
    setSection("plan");
    resetCreateFields();
    setNotice(null);
    setError(null);
  }

  function selectPlan(id: number) {
    if (busy) return;
    if (Number(managedPlanId) === id && !creating) return;
    if (!confirmDiscardDrafts()) return;
    setDraftFile(null);
    setLegacyDraftFile(null);
    setFireSafetyDraftFile(null);
    setCreating(false);
    setManagedPlanId(id);
    setNotice(null);
    setError(null);
  }

  function cancelCreate() {
    if (!confirmDiscardDrafts()) return;
    resetCreateFields();
    setNotice(null);
    if (plans.length > 0) setCreating(false);
  }

  function handleFileChange(file: File | null) {
    if (!file) {
      setDraftFile(null);
      return;
    }
    if (!isAllowedPlanFile(file)) {
      setError("Es werden nur PDF-, JPG-, PNG-, WebP- oder TIFF-Dateien unterstützt.");
      setDraftFile(null);
      return;
    }
    setDraftFile(file);
    setError(null);
    setNotice(null);
    if (!title.trim()) {
      setTitle(file.name.replace(/\.[^.]+$/, "").trim());
    }
    if (!page.trim()) setPage("1");
  }

  function handleLegacyFileChange(file: File | null) {
    if (!file) {
      setLegacyDraftFile(null);
      return;
    }
    if (!isAllowedPlanFile(file)) {
      setError("Es werden nur PDF-, JPG-, PNG-, WebP- oder TIFF-Dateien unterstützt.");
      setLegacyDraftFile(null);
      return;
    }
    setLegacyDraftFile(file);
    setError(null);
    setNotice(null);
    if (!legacyPage.trim()) setLegacyPage("1");
  }

  function handleFireSafetyFileChange(file: File | null) {
    if (!file) {
      setFireSafetyDraftFile(null);
      return;
    }
    if (!isAllowedPlanFile(file)) {
      setError("Es werden nur PDF-, JPG-, PNG-, WebP- oder TIFF-Dateien unterstützt.");
      setFireSafetyDraftFile(null);
      return;
    }
    setFireSafetyDraftFile(file);
    setError(null);
    setNotice(null);
    if (!fireSafetyPage.trim()) setFireSafetyPage("1");
  }

  async function handleLegacyUpload() {
    if (!managedPlan?.id || !legacyDraftFile) return;
    setPending("legacy-upload");
    setError(null);
    try {
      await uploadMaintenancePlanLegacy(managedPlan.id, {
        file: legacyDraftFile,
        page: Math.max(1, Number(legacyPage) || 1),
      });
      setLegacyDraftFile(null);
      await loadPlans();
      onPlansChanged();
      flash("Alter Plan hochgeladen.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Alter Plan konnte nicht hochgeladen werden.");
    } finally {
      setPending(null);
    }
  }

  async function handleLegacyDelete() {
    if (!managedPlan?.id || !managedPlan.has_legacy_plan) return;
    if (!window.confirm("Alter Plan wirklich entfernen?")) return;
    setPending("legacy-delete");
    setError(null);
    try {
      await deleteMaintenancePlanLegacy(managedPlan.id);
      setLegacyDraftFile(null);
      await loadPlans();
      onPlansChanged();
      flash("Alter Plan entfernt.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Alter Plan konnte nicht entfernt werden.");
    } finally {
      setPending(null);
    }
  }

  async function handleFireSafetyUpload() {
    if (!managedPlan?.id || !fireSafetyDraftFile) return;
    setPending("fire-upload");
    setError(null);
    try {
      await uploadMaintenancePlanFireSafety(managedPlan.id, {
        file: fireSafetyDraftFile,
        page: Math.max(1, Number(fireSafetyPage) || 1),
      });
      setFireSafetyDraftFile(null);
      await loadPlans();
      onPlansChanged();
      flash("Brandschutzkonzept hochgeladen.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Brandschutzkonzept konnte nicht hochgeladen werden.");
    } finally {
      setPending(null);
    }
  }

  async function handleFireSafetyDelete() {
    if (!managedPlan?.id || !managedPlan.has_fire_safety_plan) return;
    if (!window.confirm("Brandschutzkonzept wirklich entfernen?")) return;
    setPending("fire-delete");
    setError(null);
    try {
      await deleteMaintenancePlanFireSafety(managedPlan.id);
      setFireSafetyDraftFile(null);
      await loadPlans();
      onPlansChanged();
      flash("Brandschutzkonzept entfernt.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Brandschutzkonzept konnte nicht entfernt werden.");
    } finally {
      setPending(null);
    }
  }

  async function handleUpload() {
    if (!heroAddressId || !subLocationId || !draftFile) return;
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setError("Bitte einen Titel für den Plan angeben.");
      return;
    }
    setPending("upload");
    setError(null);
    try {
      const created = await uploadSubLocationPlan(heroAddressId, subLocationId, {
        file: draftFile,
        title: trimmedTitle,
        version: version.trim(),
        description: description.trim(),
        page: Math.max(1, Number(page) || 1),
        is_active: activateAfterUpload,
      });
      await loadPlans();
      setDraftFile(null);
      setCreating(false);
      setSection("plan");
      const createdId = Number(created?.id);
      if (Number.isFinite(createdId) && createdId > 0) setManagedPlanId(createdId);
      onPlansChanged();
      flash("Plan hochgeladen.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Plan konnte nicht hochgeladen werden.");
    } finally {
      setPending(null);
    }
  }

  async function handleSaveMeta() {
    if (!managedPlan?.id) return;
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setError("Bitte einen Titel für den Plan angeben.");
      return;
    }
    const nextPage = String(Math.max(1, Number(page) || 1));
    const nextVersion = version.trim();
    const nextDescription = description.trim();
    setPending("save");
    setError(null);
    try {
      await patchMaintenancePlan(managedPlan.id, {
        title: trimmedTitle,
        version: nextVersion,
        description: nextDescription,
        page: Number(nextPage),
        is_active: isActive,
        marker_overlay_scale: clampMarkerOverlayScale(markerScalePct / 100),
      });
      setTitle(trimmedTitle);
      setVersion(nextVersion);
      setDescription(nextDescription);
      setPage(nextPage);
      await loadPlans();
      onPlansChanged();
      flash("Änderungen gespeichert.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Plan konnte nicht gespeichert werden.");
    } finally {
      setPending(null);
    }
  }

  async function handleDelete() {
    if (!managedPlan?.id) return;
    const label = managedPlan.title || managedPlan.filename || managedPlan.id;
    if (!window.confirm(`Plan "${label}" wirklich löschen?`)) return;
    setPending("delete");
    setError(null);
    try {
      await deleteMaintenancePlan(managedPlan.id);
      await loadPlans();
      onPlansChanged();
      flash("Plan gelöscht.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Plan konnte nicht gelöscht werden.");
    } finally {
      setPending(null);
    }
  }

  const legacyPreviewImageUrl = legacyDraftFile
    ? legacyDraftPreviewUrl
    : managedPlan?.has_legacy_plan
      ? buildRenderedPlanImageUrl(
          {
            rendered_plan_image_url:
              managedPlan.legacy_plan_rendered_plan_image_url || managedPlan.legacy_plan_preview_url,
          },
          Number(managedPlan.legacy_plan_page_number || managedPlan.legacy_plan_page || 1),
        )
      : "";

  const fireSafetyPreviewImageUrl = fireSafetyDraftFile
    ? fireSafetyDraftPreviewUrl
    : managedPlan?.has_fire_safety_plan
      ? buildRenderedPlanImageUrl(
          {
            rendered_plan_image_url:
              managedPlan.fire_safety_plan_rendered_plan_image_url || managedPlan.fire_safety_plan_preview_url,
          },
          Number(managedPlan.fire_safety_plan_page_number || managedPlan.fire_safety_plan_page || 1),
        )
      : "";

  const previewImageUrl = draftFile
    ? draftPreviewUrl
    : managedPlan
      ? buildRenderedPlanImageUrl(managedPlan, Number(managedPlan.page_number || managedPlan.page || 1))
      : "";

  const hasSubLocation = Boolean(subLocationId);
  const disabled = !hasSubLocation || busy;

  const footer = renderFooter();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      dirty={createDirty || metaDirty || legacyDraftFile != null || fireSafetyDraftFile != null}
      title="Pläne verwalten"
      description={hasSubLocation ? subLocationLabel || "Ausgewählter Unterort" : "Bitte zuerst einen Unterort auswählen."}
      size="4xl"
      fullScreenMobile
      panelClassName="sm:h-[min(46rem,calc(100dvh-2rem))]"
      bodyClassName="flex min-h-0 flex-1 flex-col !overflow-hidden !p-0"
      footer={footer}
    >
      <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
        <aside className="flex max-h-56 shrink-0 flex-col border-b border-separator lg:max-h-none lg:min-h-0 lg:w-72 lg:overflow-hidden lg:border-b-0 lg:border-r">
          <div className="shrink-0 border-b border-separator p-3">
            <Button
              type="button"
              variant={creating ? "primary" : "secondary"}
              size="sm"
              className="w-full"
              disabled={!hasSubLocation || busy}
              aria-pressed={creating}
              leftIcon={<PlusIcon className="h-4 w-4" />}
              onClick={startNewPlan}
            >
              Neuer Plan
            </Button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-2" role="listbox" aria-label="Vorhandene Pläne">
            {loading && plans.length === 0 ? (
              <div className="flex items-center justify-center gap-2 px-3 py-8 text-sm text-label-secondary">
                <Spinner />
                Lade Pläne …
              </div>
            ) : orderedPlans.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-label-secondary">Noch kein Plan für diesen Unterort.</p>
            ) : (
              <div className="space-y-1">
                {orderedPlans.map((plan) => {
                  const id = Number(plan.id);
                  if (!Number.isFinite(id) || id <= 0) return null;
                  const selected = !creating && Number(managedPlanId) === id;
                  const extras = [
                    plan.has_legacy_plan ? "Alter Plan" : "",
                    plan.has_fire_safety_plan ? "Brandschutz" : "",
                  ].filter(Boolean);
                  return (
                    <button
                      key={id}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      disabled={busy}
                      className={clsx(
                        "flex w-full flex-col gap-0.5 rounded-xl px-3 py-2.5 text-left transition",
                        selected ? "bg-accent-soft ring-1 ring-inset ring-accent/35" : "hover:bg-fill-tertiary",
                      )}
                      onClick={() => selectPlan(id)}
                    >
                      <span className="flex items-center gap-2">
                        <span className="min-w-0 flex-1 truncate text-sm font-medium text-label">{planLabel(plan)}</span>
                        {plan.is_active ? (
                          <span className="shrink-0 rounded-full bg-accent px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-label-on-accent">
                            Aktiv
                          </span>
                        ) : null}
                      </span>
                      <span className="truncate text-xs text-label-tertiary">{planCardSubtitle(plan)}</span>
                      {extras.length > 0 ? (
                        <span className="truncate text-[11px] text-label-tertiary">{extras.join(" · ")}</span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </aside>

        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          {error ? (
            <p className="mx-5 mt-4 rounded-xl bg-status-red/10 px-3 py-2 text-sm text-status-red" role="alert">
              {error}
            </p>
          ) : null}

          {!hasSubLocation ? (
            <div className="flex flex-1 items-center justify-center p-8 text-center text-sm text-label-secondary">
              Bitte zuerst einen Unterort auswählen.
            </div>
          ) : creating ? (
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
              <div className="grid gap-5 p-5 lg:grid-cols-[minmax(0,1fr)_minmax(15rem,20rem)]">
                <div className="space-y-4">
                  <div>
                    <h3 className="text-base font-semibold text-label">Neuen Plan hochladen</h3>
                    <p className="mt-1 text-sm text-label-secondary">
                      Datei ablegen, Titel prüfen und hochladen. Alten Plan und Brandschutzkonzept lassen sich danach an
                      diesem Plan ergänzen.
                    </p>
                  </div>
                  <PlanDropzone
                    file={draftFile}
                    disabled={disabled}
                    title="Datei hier ablegen oder auswählen"
                    hint="PDF, JPG, PNG, WebP oder TIFF"
                    onFile={handleFileChange}
                  />
                  <TextInput
                    label="Titel"
                    value={title}
                    maxLength={200}
                    disabled={disabled}
                    inputClassName="w-full"
                    onChange={(e) => {
                      setTitle(e.target.value);
                      setNotice(null);
                    }}
                  />
                  <div className="grid gap-3 sm:grid-cols-2">
                    <TextInput
                      label="Version"
                      hint="optional"
                      value={version}
                      maxLength={80}
                      disabled={disabled}
                      inputClassName="w-full"
                      onChange={(e) => {
                        setVersion(e.target.value);
                        setNotice(null);
                      }}
                    />
                    <TextInput
                      label="Seite"
                      hint="bei PDF"
                      type="number"
                      min={1}
                      value={page}
                      disabled={disabled}
                      inputClassName="w-full"
                      onChange={(e) => {
                        setPage(e.target.value);
                        setNotice(null);
                      }}
                    />
                  </div>
                  <TextareaInput
                    label="Beschreibung"
                    hint="optional"
                    rows={3}
                    maxLength={1000}
                    value={description}
                    disabled={disabled}
                    inputClassName="w-full"
                    onChange={(e) => {
                      setDescription(e.target.value);
                      setNotice(null);
                    }}
                  />
                  <label className="flex cursor-pointer items-center justify-between gap-3 rounded-xl bg-fill-quaternary px-3 py-2.5">
                    <span>
                      <span className="block text-sm font-medium text-label">Direkt aktiv setzen</span>
                      <span className="block text-xs text-label-tertiary">Wird in der Planansicht dieses Unterorts gezeigt.</span>
                    </span>
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-accent"
                      checked={activateAfterUpload}
                      disabled={disabled}
                      onChange={(e) => setActivateAfterUpload(e.target.checked)}
                    />
                  </label>
                </div>
                <div className="lg:sticky lg:top-0">
                  <p className="mb-2 text-xs font-medium uppercase tracking-wide text-label-tertiary">Vorschau</p>
                  <PlanPreview
                    file={draftFile}
                    objectUrl={draftPreviewUrl}
                    imageUrl={draftFile ? draftPreviewUrl : ""}
                    title="Upload-Vorschau"
                    empty="Die Vorschau erscheint, sobald eine Datei gewählt ist."
                  />
                  <p className="mt-2 truncate text-xs text-label-tertiary">
                    {draftFile
                      ? `${draftFile.name} · ${formatFileSize(draftFile.size) || draftFile.type || "Datei"}`
                      : "Keine Datei gewählt."}
                  </p>
                </div>
              </div>
            </div>
          ) : !managedPlan ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 p-8 text-center">
              {loading ? <Spinner className="h-5 w-5" /> : null}
              <p className="text-sm text-label-secondary">
                {loading ? "Lade Pläne …" : "Wählen Sie links einen Plan oder laden Sie einen neuen hoch."}
              </p>
            </div>
          ) : (
            <>
              <div className="shrink-0 px-5 pb-1 pt-4">
                <div className="flex items-center gap-2">
                  <h3 className="min-w-0 flex-1 truncate text-base font-semibold text-label">{planLabel(managedPlan)}</h3>
                  {isActive ? (
                    <span className="shrink-0 rounded-full bg-accent px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-label-on-accent">
                      Aktiv
                    </span>
                  ) : null}
                </div>
                <p className="mt-0.5 truncate text-sm text-label-secondary">{planCardSubtitle(managedPlan)}</p>
              </div>
              <div className="flex shrink-0 gap-1 border-b border-separator px-3" role="tablist" aria-label="Planbereiche">
                <SectionTab active={section === "plan"} onClick={() => setSection("plan")}>
                  Plan
                </SectionTab>
                <SectionTab active={section === "legacy"} onClick={() => setSection("legacy")} marked={Boolean(managedPlan.has_legacy_plan)}>
                  Alter Plan
                </SectionTab>
                <SectionTab
                  active={section === "fire"}
                  onClick={() => setSection("fire")}
                  marked={Boolean(managedPlan.has_fire_safety_plan)}
                >
                  Brandschutz
                </SectionTab>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain" role="tabpanel">
                {section === "plan" ? (
                  <div className="grid gap-5 p-5 lg:grid-cols-[minmax(0,1fr)_minmax(15rem,20rem)]">
                    <div className="space-y-4">
                      <TextInput
                        label="Titel"
                        value={title}
                        maxLength={200}
                        disabled={disabled}
                        inputClassName="w-full"
                        onChange={(e) => {
                          setTitle(e.target.value);
                          setNotice(null);
                        }}
                      />
                      <div className="grid gap-3 sm:grid-cols-2">
                        <TextInput
                          label="Version"
                          hint="optional"
                          value={version}
                          maxLength={80}
                          disabled={disabled}
                          inputClassName="w-full"
                          onChange={(e) => {
                            setVersion(e.target.value);
                            setNotice(null);
                          }}
                        />
                        <TextInput
                          label="Seite"
                          hint="bei PDF"
                          type="number"
                          min={1}
                          value={page}
                          disabled={disabled}
                          inputClassName="w-full"
                          onChange={(e) => {
                            setPage(e.target.value);
                            setNotice(null);
                          }}
                        />
                      </div>
                      <TextareaInput
                        label="Beschreibung"
                        hint="optional"
                        rows={3}
                        maxLength={1000}
                        value={description}
                        disabled={disabled}
                        inputClassName="w-full"
                        onChange={(e) => {
                          setDescription(e.target.value);
                          setNotice(null);
                        }}
                      />
                      <label className="flex cursor-pointer items-center justify-between gap-3 rounded-xl bg-fill-quaternary px-3 py-2.5">
                        <span>
                          <span className="block text-sm font-medium text-label">Aktiver Plan</span>
                          <span className="block text-xs text-label-tertiary">Wird in der Planansicht dieses Unterorts gezeigt.</span>
                        </span>
                        <input
                          type="checkbox"
                          className="h-4 w-4 accent-accent"
                          checked={isActive}
                          disabled={disabled}
                          onChange={(e) => {
                            setIsActive(e.target.checked);
                            setNotice(null);
                          }}
                        />
                      </label>
                      <div className="rounded-xl bg-fill-quaternary px-3 py-2.5">
                        <div className="mb-2 flex items-center justify-between gap-3">
                          <span className="text-sm font-medium text-label">Marker-Größe</span>
                          <span className="text-sm tabular-nums text-label-secondary">{markerScalePct}%</span>
                        </div>
                        <input
                          className="w-full accent-accent"
                          type="range"
                          min={25}
                          max={400}
                          step={5}
                          value={markerScalePct}
                          disabled={disabled}
                          aria-label="Marker-Größe"
                          onChange={(e) => {
                            setMarkerScalePct(Number(e.target.value));
                            setNotice(null);
                          }}
                        />
                        <p className="mt-1 text-xs text-label-tertiary">Symbole und Punkte auf diesem Plan.</p>
                      </div>
                      <div className="border-t border-separator pt-3">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="!text-status-red hover:bg-status-red/10"
                          disabled={disabled}
                          loading={pending === "delete"}
                          onClick={() => void handleDelete()}
                        >
                          Diesen Plan löschen
                        </Button>
                      </div>
                    </div>
                    <div className="lg:sticky lg:top-0">
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <p className="text-xs font-medium uppercase tracking-wide text-label-tertiary">Vorschau</p>
                        {managedPlan.file_proxy_url ? (
                          <OpenFileLink href={String(managedPlan.file_proxy_url)}>Datei öffnen</OpenFileLink>
                        ) : null}
                      </div>
                      <PlanPreview
                        file={null}
                        objectUrl=""
                        imageUrl={previewImageUrl}
                        title="Plan-Vorschau"
                        empty="Für diesen Plan liegt keine Vorschau vor."
                      />
                      <p className="mt-2 truncate text-xs text-label-tertiary">
                        {managedPlan.filename || "Keine Datei"}
                        {formatFileSize(managedPlan.file_size_bytes) ? ` · ${formatFileSize(managedPlan.file_size_bytes)}` : ""}
                      </p>
                    </div>
                  </div>
                ) : null}

                {section === "legacy" ? (
                  <AttachmentEditor
                    heading="Alter Plan"
                    hint="Referenz für Monteure in der App. Offline verfügbar und in der Planansicht über „Alter Plan“ umschaltbar."
                    emptyLabel="Noch kein alter Plan hinterlegt."
                    hasFile={Boolean(managedPlan.has_legacy_plan)}
                    filename={managedPlan.legacy_plan_filename || "Alter Plan hinterlegt"}
                    pageInfo={managedPlan.legacy_plan_page || managedPlan.legacy_plan_page_number}
                    sizeLabel={formatFileSize(managedPlan.legacy_plan_file_size_bytes)}
                    fileUrl={managedPlan.legacy_plan_file_proxy_url ? String(managedPlan.legacy_plan_file_proxy_url) : ""}
                    draftFile={legacyDraftFile}
                    draftPreviewUrl={legacyDraftPreviewUrl}
                    previewImageUrl={legacyPreviewImageUrl}
                    page={legacyPage}
                    disabled={disabled}
                    removeLoading={pending === "legacy-delete"}
                    onPageChange={(value) => {
                      setLegacyPage(value);
                      setNotice(null);
                    }}
                    onFile={handleLegacyFileChange}
                    onDelete={() => void handleLegacyDelete()}
                  />
                ) : null}

                {section === "fire" ? (
                  <AttachmentEditor
                    heading="Brandschutzkonzept"
                    hint="Für Monteure in der App. Offline verfügbar und in der Planansicht über „Brandschutzkonzept“ umschaltbar."
                    emptyLabel="Noch kein Brandschutzkonzept hinterlegt."
                    hasFile={Boolean(managedPlan.has_fire_safety_plan)}
                    filename={managedPlan.fire_safety_plan_filename || "Brandschutzkonzept hinterlegt"}
                    pageInfo={managedPlan.fire_safety_plan_page || managedPlan.fire_safety_plan_page_number}
                    sizeLabel={formatFileSize(managedPlan.fire_safety_plan_file_size_bytes)}
                    fileUrl={managedPlan.fire_safety_plan_file_proxy_url ? String(managedPlan.fire_safety_plan_file_proxy_url) : ""}
                    draftFile={fireSafetyDraftFile}
                    draftPreviewUrl={fireSafetyDraftPreviewUrl}
                    previewImageUrl={fireSafetyPreviewImageUrl}
                    page={fireSafetyPage}
                    disabled={disabled}
                    removeLoading={pending === "fire-delete"}
                    onPageChange={(value) => {
                      setFireSafetyPage(value);
                      setNotice(null);
                    }}
                    onFile={handleFireSafetyFileChange}
                    onDelete={() => void handleFireSafetyDelete()}
                  />
                ) : null}
              </div>
            </>
          )}
        </div>
      </div>
    </Dialog>
  );

  function renderFooter(): ReactNode {
    if (!hasSubLocation) return null;
    if (creating) {
      return (
        <>
          {notice ? <p className="mr-auto text-sm font-medium text-status-green">{notice}</p> : <span className="mr-auto" />}
          {plans.length > 0 ? (
            <Button type="button" variant="secondary" size="sm" disabled={busy} onClick={cancelCreate}>
              Abbrechen
            </Button>
          ) : null}
          <Button
            type="button"
            size="sm"
            disabled={disabled || !draftFile || !title.trim()}
            loading={pending === "upload"}
            onClick={() => void handleUpload()}
          >
            Plan hochladen
          </Button>
        </>
      );
    }
    if (!managedPlan) return null;
    if (section === "legacy") {
      return (
        <>
          <p className="mr-auto text-sm text-label-tertiary">
            {notice ? (
              <span className="font-medium text-status-green">{notice}</span>
            ) : legacyDraftFile ? (
              "Datei gewählt, noch nicht hochgeladen."
            ) : managedPlan.has_legacy_plan ? (
              "Neue Datei ersetzt den hinterlegten alten Plan."
            ) : (
              "Datei wählen und hochladen."
            )}
          </p>
          <Button
            type="button"
            size="sm"
            disabled={disabled || !legacyDraftFile}
            loading={pending === "legacy-upload"}
            onClick={() => void handleLegacyUpload()}
          >
            Hochladen
          </Button>
        </>
      );
    }
    if (section === "fire") {
      return (
        <>
          <p className="mr-auto text-sm text-label-tertiary">
            {notice ? (
              <span className="font-medium text-status-green">{notice}</span>
            ) : fireSafetyDraftFile ? (
              "Datei gewählt, noch nicht hochgeladen."
            ) : managedPlan.has_fire_safety_plan ? (
              "Neue Datei ersetzt das hinterlegte Brandschutzkonzept."
            ) : (
              "Datei wählen und hochladen."
            )}
          </p>
          <Button
            type="button"
            size="sm"
            disabled={disabled || !fireSafetyDraftFile}
            loading={pending === "fire-upload"}
            onClick={() => void handleFireSafetyUpload()}
          >
            Hochladen
          </Button>
        </>
      );
    }
    return (
      <>
        <p className="mr-auto text-sm text-label-tertiary">
          {notice ? (
            <span className="font-medium text-status-green">{notice}</span>
          ) : metaDirty ? (
            "Ungespeicherte Änderungen"
          ) : null}
        </p>
        <Button
          type="button"
          size="sm"
          disabled={disabled || !metaDirty}
          loading={pending === "save"}
          onClick={() => void handleSaveMeta()}
        >
          Speichern
        </Button>
      </>
    );
  }
}

function planLabel(plan: MaintenancePlanRecord): string {
  return String(plan.title || plan.filename || `Plan ${plan.id}`);
}

function planCardSubtitle(plan: MaintenancePlanRecord): string {
  const bits = [
    plan.version ? `Version ${plan.version}` : "",
    plan.page || plan.page_number ? `Seite ${plan.page || plan.page_number}` : "",
    formatFileSize(plan.file_size_bytes),
    plan.filename && plan.filename !== plan.title ? plan.filename : "",
  ].filter(Boolean);
  return bits.join(" · ") || "Ohne weitere Angaben";
}

function isPdfFile(file: File | null): boolean {
  if (!file) return false;
  if (file.type === "application/pdf") return true;
  return file.name.toLowerCase().endsWith(".pdf");
}

function SectionTab({
  active,
  marked,
  onClick,
  children,
}: {
  active: boolean;
  marked?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      className={clsx(
        "inline-flex items-center gap-1.5 border-b-2 px-3 py-2.5 text-sm font-medium transition",
        active ? "border-accent text-label" : "border-transparent text-label-secondary hover:text-label",
      )}
      onClick={onClick}
    >
      {children}
      {marked ? <span className="h-1.5 w-1.5 rounded-full bg-accent" aria-hidden /> : null}
    </button>
  );
}

function OpenFileLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg px-2.5 text-sm font-medium text-label ring-1 ring-inset ring-separator-opaque hover:bg-fill-tertiary"
    >
      <ArrowTopRightOnSquareIcon className="h-4 w-4" />
      {children}
    </a>
  );
}

function PlanDropzone({
  file,
  onFile,
  disabled,
  title,
  hint,
}: {
  file: File | null;
  onFile: (file: File | null) => void;
  disabled?: boolean;
  title: string;
  hint: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept={PLAN_FILE_ACCEPT}
        className="sr-only"
        disabled={disabled}
        onChange={(e) => {
          onFile(e.target.files?.[0] ?? null);
          e.target.value = "";
        }}
      />
      <button
        type="button"
        disabled={disabled}
        data-maintenance-plan-drop=""
        className={clsx(
          "flex min-h-[9.5rem] w-full flex-col items-center justify-center rounded-2xl border border-dashed px-6 py-6 text-center transition",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-accent",
          dragOver
            ? "border-accent bg-accent/10"
            : "border-separator bg-fill-quaternary/60 hover:border-accent/70 hover:bg-accent/5",
        )}
        onClick={() => inputRef.current?.click()}
        onDragEnter={(e) => {
          if (!e.dataTransfer?.types || !Array.from(e.dataTransfer.types).includes("Files")) return;
          e.preventDefault();
          e.stopPropagation();
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (e.dataTransfer) e.dataTransfer.dropEffect = disabled ? "none" : "copy";
          if (!disabled) setDragOver(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          e.stopPropagation();
          const next = e.relatedTarget;
          if (next instanceof Node && e.currentTarget.contains(next)) return;
          setDragOver(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setDragOver(false);
          if (disabled) return;
          onFile(e.dataTransfer.files?.[0] ?? null);
        }}
      >
        <ArrowUpTrayIcon className="mb-2 h-7 w-7 text-label-tertiary" />
        {file ? (
          <>
            <p className="max-w-full truncate text-sm font-medium text-label">{file.name}</p>
            <p className="mt-1 text-xs text-label-tertiary">
              {formatFileSize(file.size) || "Datei gewählt"} · Klicken zum Ersetzen
            </p>
          </>
        ) : (
          <>
            <p className="text-sm font-medium text-label">{title}</p>
            <p className="mt-1 text-xs text-label-tertiary">{hint}</p>
          </>
        )}
      </button>
      {file ? (
        <button
          type="button"
          className="mt-2 text-xs font-medium text-label-secondary hover:text-label hover:underline"
          disabled={disabled}
          onClick={() => onFile(null)}
        >
          Datei entfernen
        </button>
      ) : null}
    </div>
  );
}

function PlanPreview({
  file,
  objectUrl,
  imageUrl,
  title,
  empty,
}: {
  file: File | null;
  objectUrl: string;
  imageUrl: string;
  title: string;
  empty: string;
}) {
  const showPdf = isPdfFile(file) && Boolean(objectUrl);
  return (
    <div className="huelle-plan-management__preview-frame">
      {showPdf ? (
        <iframe title={title} src={objectUrl} />
      ) : imageUrl ? (
        <img src={imageUrl} alt={title} />
      ) : (
        <p className="px-6 text-center text-sm text-label-secondary">{empty}</p>
      )}
    </div>
  );
}

function AttachmentEditor({
  heading,
  hint,
  emptyLabel,
  hasFile,
  filename,
  pageInfo,
  sizeLabel,
  fileUrl,
  draftFile,
  draftPreviewUrl,
  previewImageUrl,
  page,
  disabled,
  removeLoading,
  onPageChange,
  onFile,
  onDelete,
}: {
  heading: string;
  hint: string;
  emptyLabel: string;
  hasFile: boolean;
  filename: string;
  pageInfo?: number | string | null;
  sizeLabel: string;
  fileUrl: string;
  draftFile: File | null;
  draftPreviewUrl: string;
  previewImageUrl: string;
  page: string;
  disabled?: boolean;
  removeLoading?: boolean;
  onPageChange: (value: string) => void;
  onFile: (file: File | null) => void;
  onDelete: () => void;
}) {
  const meta = [pageInfo ? `Seite ${pageInfo}` : "", sizeLabel].filter(Boolean).join(" · ");
  return (
    <div className="grid gap-5 p-5 lg:grid-cols-[minmax(0,1fr)_minmax(15rem,20rem)]">
      <div className="space-y-4">
        <div>
          <h3 className="text-base font-semibold text-label">{heading}</h3>
          <p className="mt-1 text-sm text-label-secondary">{hint}</p>
        </div>
        {hasFile ? (
          <div className="flex items-center gap-3 rounded-xl bg-fill-quaternary px-3 py-2.5">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-label">{filename}</p>
              {meta ? <p className="truncate text-xs text-label-tertiary">{meta}</p> : null}
            </div>
            {fileUrl ? <OpenFileLink href={fileUrl}>Öffnen</OpenFileLink> : null}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="!text-status-red hover:bg-status-red/10"
              disabled={disabled}
              loading={removeLoading}
              onClick={onDelete}
            >
              Entfernen
            </Button>
          </div>
        ) : (
          <p className="text-sm text-label-secondary">{emptyLabel}</p>
        )}
        <PlanDropzone
          file={draftFile}
          disabled={disabled}
          title={hasFile ? "Neue Datei ablegen, um zu ersetzen" : "Datei hier ablegen oder auswählen"}
          hint="PDF, JPG, PNG, WebP oder TIFF"
          onFile={onFile}
        />
        <TextInput
          label="Seite"
          hint="Welche Seite der neuen Datei verwendet wird"
          type="number"
          min={1}
          value={page}
          disabled={disabled}
          inputClassName="w-full max-w-[10rem]"
          onChange={(e) => onPageChange(e.target.value)}
        />
      </div>
      <div className="lg:sticky lg:top-0">
        <p className="mb-2 text-xs font-medium uppercase tracking-wide text-label-tertiary">Vorschau</p>
        <PlanPreview
          file={draftFile}
          objectUrl={draftPreviewUrl}
          imageUrl={previewImageUrl}
          title={`${heading} Vorschau`}
          empty={`Vorschau des Bereichs „${heading}“.`}
        />
      </div>
    </div>
  );
}
